import React, { useEffect, useMemo, useState } from 'react';
import CollapsibleText, { COLLAPSIBLE_TEXT_PREVIEW_CHARS } from './CollapsibleText';
import LazyDetails from './LazyDetails';
import {
  classifyEvidenceFields,
  EVALUATION_EVIDENCE_INTRO,
  evidenceCardScore,
  evidenceCardTitle,
  formatEvidenceScalar,
  isMultilineEvidenceValue,
  type ClassifiedEvidenceField,
} from '../../utils/evaluationResultDisplay';

const EVIDENCE_PAGE_SIZE = 5;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function EvidenceFieldLabel({ field }: { field: ClassifiedEvidenceField }) {
  return <span className="eval-evidence-card__field-label">{field.label}</span>;
}

function EvidenceFieldHelp({ field }: { field: ClassifiedEvidenceField }) {
  if (!field.help) return null;
  return <p className="eval-evidence-card__field-help">{field.help}</p>;
}

function EvidenceValue({ field }: { field: ClassifiedEvidenceField }) {
  const { value } = field;

  if (
    (field.key === 'comment' || field.key === 'comments') &&
    (value == null || value === '' || (typeof value === 'string' && !value.trim()))
  ) {
    return <span className="eval-evidence__inline eval-evidence__inline--muted">No comment on this dataset item.</span>;
  }

  if (Array.isArray(value)) {
    if (value.every((entry) => typeof entry === 'string' || typeof entry === 'number')) {
      return (
        <ul className="eval-evidence__list">
          {value.map((entry, idx) => (
            <li key={idx}>{String(entry)}</li>
          ))}
        </ul>
      );
    }
    return (
      <div className="eval-evidence__nested">
        {value.map((entry, idx) => (
          <EvidenceItem key={idx} item={entry} index={idx} nested />
        ))}
      </div>
    );
  }

  const nested = asRecord(value);
  if (nested) {
    const nestedFields = classifyEvidenceFields(nested).filter((f) => f.group !== 'meta');
    if (nestedFields.length > 0) {
      return (
        <dl className="eval-evidence__nested-rows">
          {nestedFields.map((nestedField) => (
            <div key={nestedField.key} className="eval-evidence__nested-row">
              <dt>
                <EvidenceFieldLabel field={nestedField} />
              </dt>
              <dd>
                <EvidenceValue field={nestedField} />
                <EvidenceFieldHelp field={nestedField} />
              </dd>
            </div>
          ))}
        </dl>
      );
    }
  }

  if (isMultilineEvidenceValue(value)) {
    const text = formatEvidenceScalar(value);
    if (text.length > COLLAPSIBLE_TEXT_PREVIEW_CHARS) {
      return <CollapsibleText text={text} />;
    }
    return <pre className="eval-evidence__text">{text}</pre>;
  }

  return <span className="eval-evidence__inline">{formatEvidenceScalar(value)}</span>;
}

function TechnicalFields({ fields }: { fields: ClassifiedEvidenceField[] }) {
  return (
    <LazyDetails
      className="eval-evidence-card__technical"
      bodyClassName="eval-evidence-card__technical-body"
      summary={
        <span className="eval-evidence-card__technical-summary">
          Technical fields ({fields.length})
        </span>
      }
    >
      <dl className="eval-evidence-card__rows">
        {fields.map((field) => (
          <div key={field.key} className="eval-evidence-card__row">
            <dt>
              <EvidenceFieldLabel field={field} />
            </dt>
            <dd>
              <EvidenceValue field={field} />
              <EvidenceFieldHelp field={field} />
            </dd>
          </div>
        ))}
      </dl>
    </LazyDetails>
  );
}

const EvidenceItem = React.memo(function EvidenceItem({
  item,
  index,
  nested = false,
}: {
  item: unknown;
  index: number;
  nested?: boolean;
}) {
  const record = useMemo(() => asRecord(item), [item]);
  const fields = useMemo(
    () => (record ? classifyEvidenceFields(record) : []),
    [record]
  );

  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    const text = formatEvidenceScalar(item);
    return (
      <div className={`eval-evidence-card${nested ? ' eval-evidence-card--nested' : ''}`}>
        {typeof item === 'string' && item.length > COLLAPSIBLE_TEXT_PREVIEW_CHARS ? (
          <CollapsibleText text={text} />
        ) : (
          <pre className="eval-evidence__text">{text}</pre>
        )}
      </div>
    );
  }

  if (!record) {
    return (
      <div className={`eval-evidence-card${nested ? ' eval-evidence-card--nested' : ''}`}>
        <pre className="eval-evidence__text">{formatEvidenceScalar(item)}</pre>
      </div>
    );
  }

  const primary = fields.filter((f) => f.group === 'primary');
  const meta = fields.filter((f) => f.group === 'meta');
  const technical = fields.filter((f) => f.group === 'technical');
  const score = evidenceCardScore(record);
  const title = nested ? `Item ${index + 1}` : evidenceCardTitle(record, index);

  const bodyFields = primary.filter(
    (f) => !['item_id', 'example_id', 'dialog_id', 'id', 'score'].includes(f.key)
  );

  return (
    <article className={`eval-evidence-card${nested ? ' eval-evidence-card--nested' : ''}`}>
      <header className="eval-evidence-card__head">
        <h6 className="eval-evidence-card__title">{title}</h6>
        {score ? <span className="eval-evidence-card__score">Score {score}</span> : null}
      </header>

      {bodyFields.length > 0 ? (
        <dl className="eval-evidence-card__rows">
          {bodyFields.map((field) => (
            <div key={field.key} className="eval-evidence-card__row">
              <dt>
                <EvidenceFieldLabel field={field} />
              </dt>
              <dd>
                <EvidenceValue field={field} />
                <EvidenceFieldHelp field={field} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {meta.length > 0 ? (
        <div className="eval-evidence-card__meta">
          {meta.map((field) => (
            <span key={field.key}>
              <strong>{field.label}:</strong> {formatEvidenceScalar(field.value)}
            </span>
          ))}
        </div>
      ) : null}

      {technical.length > 0 ? <TechnicalFields fields={technical} /> : null}
    </article>
  );
});

export interface EvaluationEvidenceListProps {
  items: unknown[];
}

const EvaluationEvidenceList: React.FC<EvaluationEvidenceListProps> = ({ items }) => {
  const normalized = useMemo(() => items.filter((item) => item != null), [items]);
  const [visibleCount, setVisibleCount] = useState(EVIDENCE_PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(EVIDENCE_PAGE_SIZE);
  }, [items]);

  if (normalized.length === 0) {
    return <p className="eval-evidence__empty">No evidence items.</p>;
  }

  const visibleItems = normalized.slice(0, visibleCount);
  const hiddenCount = normalized.length - visibleItems.length;

  return (
    <div className="eval-evidence-list">
      {visibleItems.map((item, index) => (
        <EvidenceItem key={index} item={item} index={index} />
      ))}
      <p className="eval-evidence-list__intro">{EVALUATION_EVIDENCE_INTRO}</p>
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="eval-evidence-list__more"
          onClick={() => setVisibleCount((count) => count + EVIDENCE_PAGE_SIZE)}
        >
          Show {Math.min(hiddenCount, EVIDENCE_PAGE_SIZE)} more
          {hiddenCount > EVIDENCE_PAGE_SIZE ? ` (${hiddenCount} remaining)` : ''}
        </button>
      ) : null}
      {visibleCount > EVIDENCE_PAGE_SIZE && hiddenCount === 0 ? (
        <button
          type="button"
          className="eval-evidence-list__more eval-evidence-list__more--secondary"
          onClick={() => setVisibleCount(EVIDENCE_PAGE_SIZE)}
        >
          Show fewer items
        </button>
      ) : null}
    </div>
  );
};

export default EvaluationEvidenceList;
